import joi from 'joi';
import {
  Box,
  Checkbox,
  FormGroup,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { Delete } from '@mui/icons-material';
import { InteractionsListAttributes } from '@loyalty/editor-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SwitchField } from '../../../../shared/forms/fields/SwitchField';
import { Trait } from '../utils/Trait';
import { TraitFormValue } from '../types';
import {
  formatInteractionLabel,
  isInteractionSelectable,
} from '../../../../interactions/interaction-utils';
import { useInteractions } from '../../../../interactions/use-interactions';

type FormValue = TraitFormValue<InteractionsListAttributes, void>;

export const interactionsListWidgetTraitsSchema = joi.object<TraitFormValue>({
  attributes: joi.object<InteractionsListAttributes>({
    interactionIds: joi.array().items(joi.string().uuid()).default([]),
    showLabel: joi.boolean().default(false),
  }),
});

export function InteractionsListWidgetTraits() {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<FormValue>();
  const { data: interactions = [] } = useInteractions({ withDeleted: true });
  const selectedIds = watch('attributes.interactionIds') ?? [];
  const selectedInteractions = useMemo(
    () => interactions.filter(interaction => selectedIds.includes(interaction.id)),
    [interactions, selectedIds],
  );

  const removeInteraction = (interactionId: string) => {
    setValue(
      'attributes.interactionIds',
      selectedIds.filter(id => id !== interactionId),
    );
  };

  return (
    <FormGroup>
      <Trait>
        <Controller
          control={control}
          name="attributes.interactionIds"
          defaultValue={[]}
          render={({ field: { ref, ...field } }) => (
            <TextField
              {...field}
              inputRef={ref}
              label={t('editor.interactions-list.traits.interactions')}
              select
              fullWidth
              size="small"
              helperText={t('editor.interactions-list.traits.helper')}
              SelectProps={{
                multiple: true,
                displayEmpty: true,
                renderValue: selected => {
                  const count = (selected as string[]).length;
                  if (!count) {
                    return (
                      <Typography component="span" color="text.secondary" variant="body2">
                        {t('editor.interactions-list.traits.placeholder')}
                      </Typography>
                    );
                  }

                  return t('editor.interactions-list.traits.selectedCount', { count });
                },
              }}
              value={field.value ?? []}
            >
              {interactions.length ? (
                interactions
                  .filter(interaction => isInteractionSelectable(interaction, field.value))
                  .map(interaction => (
                    <MenuItem key={interaction.id} value={interaction.id} dense>
                      <Checkbox
                        size="small"
                        checked={(field.value ?? []).includes(interaction.id)}
                        sx={{ p: 0.5, mr: 1 }}
                      />
                      <Typography color={interaction.deletedAt ? 'text.secondary' : undefined}>
                        {formatInteractionLabel(interaction, t)}
                      </Typography>
                    </MenuItem>
                  ))
              ) : (
                <MenuItem disabled dense>
                  {t('editor.interactions-list.traits.empty')}
                </MenuItem>
              )}
            </TextField>
          )}
        />
      </Trait>

      {selectedInteractions.length > 0 ? (
        <Trait>
          <Typography>{t('editor.interactions-list.traits.selectedList')}</Typography>
          <Stack spacing={0.5} mt={1}>
            {selectedInteractions.map(interaction => (
              <Box
                key={interaction.id}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <Typography>{formatInteractionLabel(interaction, t)}</Typography>
                <IconButton
                  aria-label={t('editor.interactions-list.traits.remove', {
                    name: interaction.name,
                  })}
                  edge="end"
                  onClick={() => removeInteraction(interaction.id)}
                  size="small"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>
        </Trait>
      ) : null}

      <Trait>
        <SwitchField<FormValue>
          name="attributes.showLabel"
          label={t('editor.interactions-list.traits.showLabel')}
        />
      </Trait>
    </FormGroup>
  );
}
